/**
 * @legacy CalendarSection.jsx (1900 linhas)
 *
 * Componente preservado pós-UI-2 pra back-compat. Hash legado #calendar
 * redireciona pra #rotina/agenda (UI-2). NÃO está montado no menu atual.
 *
 * SEC-1 (T8, 2026-04-30): fetches ressuscitados pra usar Authorization
 * header via useAuth(). Backend agora exige JWT em /api/calendar/*,
 * /api/tasks, /api/reminders, /api/finance/transactions, /api/habits,
 * /api/auth/google/*. Sem JWT, qualquer fetch retorna 401.
 *
 * Defesa em profundidade: este componente NÃO pode vazar dados mesmo
 * se acidentalmente ressuscitar via hash bookmark.
 *
 * Removal definitivo: sessão futura de housekeeping.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Calendar, Clock, MapPin, Users, ChevronLeft, ChevronRight,
  Search, Filter, Loader2, ExternalLink, Trash2, Edit3, X,
  Link2, AlertCircle, CalendarDays, CalendarRange, List, Grid3X3,
  CheckSquare, Bell, Target
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  addDays, addWeeks, addMonths, subWeeks, subMonths, isSameDay, isToday,
  isTomorrow, isPast, differenceInMinutes } from "date-fns";
import { ptBR } from "date-fns/locale";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// SEC-1: helper pra montar headers com JWT. Lê direto de localStorage
// pra evitar refator em todas as funções fetch deste componente legacy.
// Mesma chave usada pelo useAuth (nexo_token).
function _authHeaders() {
  const token = (() => {
    try { return localStorage.getItem("nexo_token"); }
    catch { return null; }
  })();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ==================== UTILITY FUNCTIONS ====================

const formatEventTime = (dateStr, isAllDay) => {
  if (isAllDay) return "Dia todo";
  try {
    const date = parseISO(dateStr);
    return format(date, "HH:mm", { locale: ptBR });
  } catch {
    return "";
  }
};

const formatEventDate = (dateStr) => {
  try {
    const date = parseISO(dateStr);
    if (isToday(date)) return "Hoje";
    if (isTomorrow(date)) return "Amanhã";
    return format(date, "EEE, dd MMM", { locale: ptBR });
  } catch {
    return dateStr;
  }
};

const getEventDuration = (start, end) => {
  try {
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    const mins = differenceInMinutes(endDate, startDate);
    if (mins < 60) return `${mins}min`;
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return remainingMins > 0 ? `${hours}h ${remainingMins}min` : `${hours}h`;
  } catch {
    return "";
  }
};

// ==================== CONNECT PROMPT ====================

function ConnectPrompt({ userId, onConnect, isConfigured = true }) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  const handleConnect = async () => {
    if (!isConfigured) {
      setError("Configure as credenciais OAuth nos Secrets do projeto antes de conectar.");
      return;
    }
    setIsConnecting(true);
    setError(null);

    // SEC-1 fix: chama /start COM Authorization header, recebe authorization_url
    // do Google e só então redireciona. Redirect direto pro endpoint do backend
    // perdia o JWT e caía em 401.
    try {
      const r = await fetch(
        `${API_URL}/api/auth/google/start?user_id=${userId}`,
        { headers: _authHeaders() }
      );
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setError(data?.detail || `Erro ${r.status} ao iniciar OAuth`);
        setIsConnecting(false);
        return;
      }
      const data = await r.json();
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        setError("Backend não retornou authorization_url");
        setIsConnecting(false);
      }
    } catch (e) {
      setError(e?.message || "Falha de rede ao conectar");
      setIsConnecting(false);
    }
  };

  // Check for error in URL params (from OAuth callback)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleError = params.get("google_error");
    if (googleError) {
      // Handle specific error codes with user-friendly messages
      let errorMessage = decodeURIComponent(googleError);
      if (googleError === "credentials_not_configured") {
        errorMessage = "Credenciais OAuth não configuradas. Adicione GOOGLE_CLIENT_ID e GOOGLE_CLIENT_SECRET nos Secrets do projeto.";
      }
      setError(errorMessage);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Different UI states based on configuration
  if (!isConfigured) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-nectar-card border border-amber-500/30 rounded-2xl p-6"
      >
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-7 h-7 text-amber-400" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold text-white mb-2">
              Configuração Necessária
            </h3>
            
            <p className="text-sm text-slate-400 mb-4">
              Para ativar a integração com o Google Calendar, configure as credenciais OAuth:
            </p>

            <div className="bg-nectar-bg border border-nectar-border rounded-xl p-4 mb-4 text-sm">
              <p className="text-slate-300 mb-2 font-medium">No painel do Emergent, adicione em <span className="text-amber-400">Secrets</span>:</p>
              <ul className="space-y-1 text-slate-400">
                <li>• <code className="text-brand-cyan bg-brand-cyan/10 px-1.5 py-0.5 rounded">GOOGLE_CLIENT_ID</code></li>
                <li>• <code className="text-brand-cyan bg-brand-cyan/10 px-1.5 py-0.5 rounded">GOOGLE_CLIENT_SECRET</code></li>
              </ul>
              <p className="text-xs text-slate-500 mt-3">
                Obtenha essas credenciais em <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-brand-cyan hover:underline">Google Cloud Console</a>
              </p>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              Aguardando configuração das credenciais...
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Credentials are configured - show connect button
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-nectar-card border border-brand-cyan/30 rounded-2xl p-6"
    >
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-brand-cyan/10 border border-brand-cyan/30 flex items-center justify-center flex-shrink-0">
          <Calendar className="w-7 h-7 text-brand-cyan" />
        </div>
        
        <div className="flex-1">
          <h3 className="font-display text-lg font-bold text-white mb-2">
            Google Calendar
          </h3>
          
          <p className="text-sm text-slate-400 mb-4">
            Credenciais configuradas! Conecte sua conta Google para sincronizar eventos.
          </p>

          {error && (
            <div className="flex items-start gap-2 p-3 mb-4 bg-status-error/10 border border-status-error/30 rounded-lg">
              <AlertCircle className="w-4 h-4 text-status-error flex-shrink-0 mt-0.5" />
              <p className="text-xs text-status-error">{error}</p>
            </div>
          )}

          <motion.button
            onClick={handleConnect}
            disabled={isConnecting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-4 py-2.5 bg-brand-cyan/10 border border-brand-cyan/30 text-brand-cyan rounded-xl text-sm font-medium hover:bg-brand-cyan/20 transition-all flex items-center gap-2 disabled:opacity-50"
            data-testid="connect-google-calendar-btn"
          >
            {isConnecting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Redirecionando para o Google...
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4" />
                Conectar Google Calendar
              </>
            )}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}

// ==================== SUMMARY CARD ====================

function SummaryCard({ title, value, subtitle, icon: Icon, color = "cyan", onClick }) {
  const colorClasses = {
    cyan: { bg: "bg-brand-cyan/10", text: "text-brand-cyan", border: "border-brand-cyan/30", glow: "hover:shadow-glow-cyan" },
    purple: { bg: "bg-brand-purple/10", text: "text-brand-purple", border: "border-brand-purple/30", glow: "hover:shadow-glow-purple" },
    green: { bg: "bg-brand-green/10", text: "text-brand-green", border: "border-brand-green/30", glow: "hover:shadow-glow-green" },
    amber: { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30", glow: "hover:shadow-lg hover:shadow-amber-500/10" },
  };
  const colors = colorClasses[color] || colorClasses.cyan;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, scale: 1.01 }}
      onClick={onClick}
      className={`bg-nectar-card border border-nectar-border rounded-xl p-5 cursor-pointer hover:${colors.border} ${colors.glow} transition-all duration-300`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${colors.text}`} />
        </div>
      </div>
      <h3 className="text-2xl font-bold text-white mb-1">{value}</h3>
      <p className="text-sm text-slate-400">{title}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
    </motion.div>
  );
}

// ==================== TASK CARD (For Calendar) ====================

function TaskCard({ task, compact = false }) {
  const statusColors = {
    todo: { bg: "bg-slate-500/10", text: "text-slate-400", dot: "bg-slate-400" },
    doing: { bg: "bg-brand-cyan/10", text: "text-brand-cyan", dot: "bg-brand-cyan" },
    done: { bg: "bg-brand-green/10", text: "text-brand-green", dot: "bg-brand-green" },
  };
  const colors = statusColors[task.status] || statusColors.todo;

  const formatTaskTime = (datetime) => {
    if (!datetime) return null;
    try {
      const date = parseISO(datetime);
      return format(date, "HH:mm", { locale: ptBR });
    } catch { return null; }
  };

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className={`flex items-center gap-3 p-3 rounded-xl ${colors.bg}`}
      >
        <div className={`w-1 h-10 rounded-full ${colors.dot}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${task.status === "done" ? "line-through text-slate-500" : "text-white"}`}>
            {task.title}
          </p>
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <CheckSquare className="w-3 h-3" />
            Tarefa {formatTaskTime(task.datetime) && `• ${formatTaskTime(task.datetime)}`}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-nectar-card border border-nectar-border rounded-xl p-4 hover:border-white/10 transition-all`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0`}>
          <CheckSquare className={`w-5 h-5 ${colors.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`font-medium mb-1 ${task.status === "done" ? "line-through text-slate-500" : "text-white"}`}>
            {task.title}
          </h4>
          <p className="text-xs text-slate-500">
            {task.datetime ? formatEventDate(task.datetime) : "Sem data"}
            {formatTaskTime(task.datetime) && ` às ${formatTaskTime(task.datetime)}`}
          </p>
        </div>
        <span className={`px-2 py-1 rounded-lg text-xs ${colors.bg} ${colors.text}`}>
          {task.status === "todo" ? "A fazer" : task.status === "doing" ? "Fazendo" : "Concluída"}
        </span>
      </div>
    </motion.div>
  );
}

// ==================== REMINDER CARD (For Calendar) ====================

function ReminderCard({ reminder, compact = false }) {
  const isPastReminder = reminder.datetime ? isPast(parseISO(reminder.datetime)) : false;
  const isCompleted = reminder.completed;

  const formatReminderTime = (datetime) => {
    if (!datetime) return null;
    try {
      const date = parseISO(datetime);
      return format(date, "HH:mm", { locale: ptBR });
    } catch { return null; }
  };

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className={`flex items-center gap-3 p-3 rounded-xl ${
          isCompleted ? "bg-brand-green/10" : isPastReminder ? "bg-status-error/10" : "bg-brand-purple/10"
        }`}
      >
        <div className={`w-1 h-10 rounded-full ${
          isCompleted ? "bg-brand-green" : isPastReminder ? "bg-status-error" : "bg-brand-purple"
        }`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${isCompleted ? "line-through text-slate-500" : "text-white"}`}>
            {reminder.message}
          </p>
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <Bell className="w-3 h-3" />
            Lembrete {formatReminderTime(reminder.datetime) && `• ${formatReminderTime(reminder.datetime)}`}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-nectar-card border border-nectar-border rounded-xl p-4 hover:border-white/10 transition-all`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isCompleted ? "bg-brand-green/10" : isPastReminder ? "bg-status-error/10" : "bg-brand-purple/10"
        }`}>
          <Bell className={`w-5 h-5 ${
            isCompleted ? "text-brand-green" : isPastReminder ? "text-status-error" : "text-brand-purple"
          }`} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`font-medium mb-1 ${isCompleted ? "line-through text-slate-500" : "text-white"}`}>
            {reminder.message}
          </h4>
          <p className="text-xs text-slate-500">
            {reminder.datetime ? formatEventDate(reminder.datetime) : "Sem data"}
            {formatReminderTime(reminder.datetime) && ` às ${formatReminderTime(reminder.datetime)}`}
          </p>
        </div>
        {isPastReminder && !isCompleted && (
          <span className="px-2 py-1 rounded-lg text-xs bg-status-error/10 text-status-error">
            Atrasado
          </span>
        )}
      </div>
    </motion.div>
  );
}

// ==================== EVENT CARD ====================

function EventCard({ event, onClick, onDelete, compact = false }) {
  const [showMenu, setShowMenu] = useState(false);
  const isPastEvent = isPast(parseISO(event.end || event.start));

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ x: 4 }}
        onClick={() => onClick?.(event)}
        className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
          isPastEvent ? "bg-white/[0.02] opacity-60" : "bg-white/[0.03] hover:bg-white/[0.06]"
        }`}
      >
        <div className="w-1 h-10 rounded-full bg-brand-cyan" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-medium truncate">{event.summary}</p>
          <p className="text-xs text-slate-500">
            {formatEventTime(event.start, event.is_all_day)}
            {!event.is_all_day && ` • ${getEventDuration(event.start, event.end)}`}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`relative bg-nectar-card border border-nectar-border rounded-xl p-4 hover:border-brand-cyan/30 transition-all group ${
        isPastEvent ? "opacity-60" : ""
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Time indicator */}
        <div className="w-16 text-center flex-shrink-0">
          <p className="text-lg font-bold text-brand-cyan">
            {formatEventTime(event.start, event.is_all_day)}
          </p>
          <p className="text-xs text-slate-500">
            {getEventDuration(event.start, event.end)}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-medium mb-1 truncate">{event.summary}</h4>
          
          {event.location && (
            <p className="flex items-center gap-1 text-xs text-slate-500 mb-1">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{event.location}</span>
            </p>
          )}
          
          {event.description && (
            <p className="text-xs text-slate-400 line-clamp-2">{event.description}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {event.html_link && (
            <a
              href={event.html_link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-2 text-slate-500 hover:text-brand-cyan rounded-lg hover:bg-white/5 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete?.(event); }}
            className="p-2 text-slate-500 hover:text-status-error rounded-lg hover:bg-white/5 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ==================== NEXT EVENT HIGHLIGHT ====================

function NextEventHighlight({ event }) {
  if (!event) {
    return (
      <div className="bg-nectar-card border border-nectar-border rounded-xl p-6 text-center">
        <Calendar className="w-8 h-8 text-slate-500 mx-auto mb-2" />
        <p className="text-slate-400">Nenhum evento próximo</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-gradient-to-br from-brand-cyan/10 to-brand-purple/10 border border-brand-cyan/30 rounded-xl p-6"
    >
      <p className="text-xs text-brand-cyan uppercase tracking-wider mb-2">Próximo Evento</p>
      <h3 className="text-xl font-bold text-white mb-2">{event.summary}</h3>
      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-300">
        <span className="flex items-center gap-1">
          <Calendar className="w-4 h-4 text-brand-cyan" />
          {formatEventDate(event.start)}
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-4 h-4 text-brand-cyan" />
          {formatEventTime(event.start, event.is_all_day)}
        </span>
        {event.location && (
          <span className="flex items-center gap-1">
            <MapPin className="w-4 h-4 text-brand-cyan" />
            {event.location}
          </span>
        )}
      </div>
    </motion.div>
  );
}

// ==================== CREATE EVENT MODAL ====================

function CreateEventModal({ isOpen, onClose, onCreated, userId }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [isAllDay, setIsAllDay] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Título é obrigatório");
      return;
    }

    setIsSubmitting(true);
    try {
      const startDateTime = isAllDay ? date : `${date}T${startTime}:00-03:00`;
      const endDateTime = isAllDay ? date : `${date}T${endTime}:00-03:00`;

      const response = await fetch(`${API_URL}/api/calendar/events?user_id=${userId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ..._authHeaders() },
        body: JSON.stringify({
          summary: title,
          start_datetime: startDateTime,
          end_datetime: endDateTime,
          description: description || undefined,
          location: location || undefined,
          is_all_day: isAllDay
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Evento criado com sucesso!");
        onCreated?.(data);
        onClose();
        // Reset form
        setTitle("");
        setLocation("");
        setDescription("");
        setIsAllDay(false);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Erro ao criar evento");
      }
    } catch (err) {
      toast.error("Falha na conexão");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="create-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            key="create-modal-content"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-nectar-card border border-nectar-border rounded-2xl p-6 safe-modal-pb w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl font-bold text-white">Criar Evento</h3>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/5">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Título *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: Reunião de alinhamento"
                  className="w-full px-4 py-3 bg-nectar-bg border border-nectar-border rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-brand-cyan/50 transition-colors"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Data</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-4 py-3 bg-nectar-bg border border-nectar-border rounded-xl text-white focus:outline-none focus:border-brand-cyan/50 transition-colors"
                />
              </div>

              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  id="allDay"
                  checked={isAllDay}
                  onChange={(e) => setIsAllDay(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-nectar-bg text-brand-cyan focus:ring-brand-cyan"
                />
                <label htmlFor="allDay" className="text-sm text-slate-400">Dia inteiro</label>
              </div>

              {!isAllDay && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1.5">Início</label>
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-full px-4 py-3 bg-nectar-bg border border-nectar-border rounded-xl text-white focus:outline-none focus:border-brand-cyan/50 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-1.5">Término</label>
                    <input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="w-full px-4 py-3 bg-nectar-bg border border-nectar-border rounded-xl text-white focus:outline-none focus:border-brand-cyan/50 transition-colors"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Local (opcional)</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Ex: Sala de reuniões"
                  className="w-full px-4 py-3 bg-nectar-bg border border-nectar-border rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-brand-cyan/50 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Descrição (opcional)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Detalhes do evento..."
                  rows={3}
                  className="w-full px-4 py-3 bg-nectar-bg border border-nectar-border rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-brand-cyan/50 transition-colors resize-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 rounded-xl border border-nectar-border text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={!title.trim() || isSubmitting}
                  className="flex-1 px-4 py-3 rounded-xl bg-brand-cyan/10 border border-brand-cyan/30 text-brand-cyan hover:bg-brand-cyan/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Criar Evento
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ==================== EVENT DETAIL MODAL ====================

function EventDetailModal({ event, isOpen, onClose, onDelete, onEdit }) {
  if (!event) return null;

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="event-detail-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            key="event-detail-content"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-nectar-card border border-nectar-border rounded-2xl p-6 safe-modal-pb w-full max-w-lg"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs text-brand-cyan uppercase tracking-wider mb-1">Detalhes do Evento</p>
                <h3 className="font-display text-xl font-bold text-white">{event.summary}</h3>
              </div>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/5">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-3 text-slate-300">
                <Calendar className="w-5 h-5 text-brand-cyan" />
                <span>{formatEventDate(event.start)}</span>
              </div>
              
              <div className="flex items-center gap-3 text-slate-300">
                <Clock className="w-5 h-5 text-brand-cyan" />
                <span>
                  {formatEventTime(event.start, event.is_all_day)}
                  {!event.is_all_day && ` - ${formatEventTime(event.end, false)}`}
                  {!event.is_all_day && ` (${getEventDuration(event.start, event.end)})`}
                </span>
              </div>

              {event.location && (
                <div className="flex items-center gap-3 text-slate-300">
                  <MapPin className="w-5 h-5 text-brand-cyan" />
                  <span>{event.location}</span>
                </div>
              )}

              {event.description && (
                <div className="pt-2 border-t border-nectar-border">
                  <p className="text-sm text-slate-400 whitespace-pre-wrap">{event.description}</p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              {event.html_link && (
                <a
                  href={event.html_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-4 py-3 rounded-xl border border-nectar-border text-slate-400 hover:text-white hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir no Google
                </a>
              )}
              <button
                onClick={() => onDelete?.(event)}
                className="flex-1 px-4 py-3 rounded-xl bg-status-error/10 border border-status-error/30 text-status-error hover:bg-status-error/20 transition-all flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Excluir
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ==================== DAY DETAIL MODAL ====================

function DayDetailModal({ day, items, isOpen, onClose, onEventClick }) {
  if (!day || !items) return null;

  const formatTime = (datetime) => {
    if (!datetime) return null;
    try {
      return format(parseISO(datetime), "HH:mm", { locale: ptBR });
    } catch { return null; }
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="day-detail-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            key="day-detail-content"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-nectar-card border border-nectar-border rounded-2xl p-6 safe-modal-pb w-full max-w-lg max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs text-brand-cyan uppercase tracking-wider mb-1">Agenda do dia</p>
                <h3 className="font-display text-xl font-bold text-white">
                  {format(day, "EEEE, dd 'de' MMMM", { locale: ptBR })}
                </h3>
              </div>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/5">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Tasks */}
              {items.tasks?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${CATEGORY_COLORS.tasks.color}`} />
                    <h4 className="text-sm font-medium text-white">Tarefas ({items.tasks.length})</h4>
                  </div>
                  <div className="space-y-2">
                    {items.tasks.map((task, idx) => (
                      <div key={idx} className="p-3 bg-brand-cyan/5 border border-brand-cyan/20 rounded-lg">
                        <p className="text-sm text-white">{task.title}</p>
                        {task.datetime && (
                          <p className="text-xs text-slate-500 mt-1">{formatTime(task.datetime)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reminders */}
              {items.reminders?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${CATEGORY_COLORS.reminders.color}`} />
                    <h4 className="text-sm font-medium text-white">Lembretes ({items.reminders.length})</h4>
                  </div>
                  <div className="space-y-2">
                    {items.reminders.map((reminder, idx) => (
                      <div key={idx} className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                        <p className="text-sm text-white">{reminder.message}</p>
                        {reminder.datetime && (
                          <p className="text-xs text-slate-500 mt-1">{formatTime(reminder.datetime)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Events */}
              {items.events?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${CATEGORY_COLORS.events.color}`} />
                    <h4 className="text-sm font-medium text-white">Eventos ({items.events.length})</h4>
                  </div>
                  <div className="space-y-2">
                    {items.events.map((event, idx) => (
                      <div 
                        key={idx} 
                        onClick={() => onEventClick?.(event)}
                        className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg cursor-pointer hover:bg-blue-500/10 transition-colors"
                      >
                        <p className="text-sm text-white">{event.summary}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {formatEventTime(event.start, event.is_all_day)}
                          {event.location && ` • ${event.location}`}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Income */}
              {items.income?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${CATEGORY_COLORS.income.color}`} />
                    <h4 className="text-sm font-medium text-white">Receitas ({items.income.length})</h4>
                  </div>
                  <div className="space-y-2">
                    {items.income.map((t, idx) => (
                      <div key={idx} className="p-3 bg-brand-green/5 border border-brand-green/20 rounded-lg">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-white">{t.description || t.category || "Receita"}</p>
                          <p className="text-sm text-brand-green font-medium">+R$ {t.amount?.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expense */}
              {items.expense?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${CATEGORY_COLORS.expense.color}`} />
                    <h4 className="text-sm font-medium text-white">Despesas ({items.expense.length})</h4>
                  </div>
                  <div className="space-y-2">
                    {items.expense.map((t, idx) => (
                      <div key={idx} className="p-3 bg-status-error/5 border border-status-error/20 rounded-lg">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-white">{t.description || t.category || "Despesa"}</p>
                          <p className="text-sm text-status-error font-medium">-R$ {t.amount?.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {Object.values(items).every(arr => !arr || arr.length === 0) && (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">Nenhum item para este dia</p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ==================== WEEK VIEW ====================

function WeekView({ events, currentDate, onDateChange, onEventClick }) {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const getEventsForDay = (day) => {
    return events.filter(event => {
      try {
        const eventDate = parseISO(event.start);
        return isSameDay(eventDate, day);
      } catch {
        return false;
      }
    });
  };

  return (
    <div className="space-y-4">
      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => onDateChange(subWeeks(currentDate, 1))}
          className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="text-lg font-medium text-white">
          {format(weekStart, "dd MMM", { locale: ptBR })} - {format(addDays(weekStart, 6), "dd MMM yyyy", { locale: ptBR })}
        </h3>
        <button
          onClick={() => onDateChange(addWeeks(currentDate, 1))}
          className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Week Grid */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((day, idx) => {
          const dayEvents = getEventsForDay(day);
          const isCurrentDay = isToday(day);

          return (
            <div key={idx} className="min-h-[120px]">
              <div className={`text-center py-2 rounded-lg mb-2 ${
                isCurrentDay ? "bg-brand-cyan/10 border border-brand-cyan/30" : "bg-white/[0.02]"
              }`}>
                <p className="text-xs text-slate-500">{format(day, "EEE", { locale: ptBR })}</p>
                <p className={`text-lg font-bold ${isCurrentDay ? "text-brand-cyan" : "text-white"}`}>
                  {format(day, "dd")}
                </p>
              </div>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((event, eventIdx) => (
                  <motion.div
                    key={event.id || eventIdx}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => onEventClick?.(event)}
                    className="px-2 py-1 bg-brand-cyan/10 border border-brand-cyan/20 rounded text-xs text-brand-cyan truncate cursor-pointer hover:bg-brand-cyan/20 transition-colors"
                  >
                    {formatEventTime(event.start, event.is_all_day)} {event.summary}
                  </motion.div>
                ))}
                {dayEvents.length > 3 && (
                  <p className="text-xs text-slate-500 text-center">+{dayEvents.length - 3} mais</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ==================== MONTH VIEW (PREMIUM) ====================

// Category filter colors
const CATEGORY_COLORS = {
  tasks: { color: "bg-brand-cyan", label: "Tarefas", textColor: "text-brand-cyan" },
  reminders: { color: "bg-amber-500", label: "Lembretes", textColor: "text-amber-500" },
  habits: { color: "bg-brand-purple", label: "Hábitos", textColor: "text-brand-purple" },
  events: { color: "bg-blue-500", label: "Eventos", textColor: "text-blue-500" },
  income: { color: "bg-brand-green", label: "Receitas", textColor: "text-brand-green" },
  expense: { color: "bg-status-error", label: "Despesas", textColor: "text-status-error" },
};

function MonthView({ 
  events = [], 
  tasks = [], 
  reminders = [], 
  habits = [],
  transactions = [],
  currentDate, 
  onDateChange, 
  onDayClick,
  activeFilters = ["tasks", "reminders", "habits", "events", "income", "expense"]
}) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
  
  // Generate 6 weeks of days
  const days = Array.from({ length: 42 }, (_, i) => addDays(startDate, i));

  // Get items for a specific day
  const getItemsForDay = (day) => {
    const items = {
      tasks: [],
      reminders: [],
      habits: [],
      events: [],
      income: [],
      expense: []
    };

    // Filter tasks for this day
    if (activeFilters.includes("tasks")) {
      items.tasks = tasks.filter(t => {
        if (!t.datetime) return false;
        try { return isSameDay(parseISO(t.datetime), day); } catch { return false; }
      });
    }

    // Filter reminders for this day
    if (activeFilters.includes("reminders")) {
      items.reminders = reminders.filter(r => {
        if (!r.datetime) return false;
        try { return isSameDay(parseISO(r.datetime), day); } catch { return false; }
      });
    }

    // Filter events for this day
    if (activeFilters.includes("events")) {
      items.events = events.filter(e => {
        try { return isSameDay(parseISO(e.start), day); } catch { return false; }
      });
    }

    // Filter transactions for this day
    if (activeFilters.includes("income") || activeFilters.includes("expense")) {
      transactions.forEach(t => {
        try {
          if (isSameDay(parseISO(t.created_at || t.date), day)) {
            if (t.type === "income" && activeFilters.includes("income")) {
              items.income.push(t);
            } else if (t.type === "expense" && activeFilters.includes("expense")) {
              items.expense.push(t);
            }
          }
        } catch {}
      });
    }

    return items;
  };

  const isCurrentMonth = (day) => {
    return day >= monthStart && day <= monthEnd;
  };

  // Count total items for a day
  const getTotalItems = (items) => {
    return Object.values(items).reduce((sum, arr) => sum + arr.length, 0);
  };

  return (
    <div className="space-y-6">
      {/* Navigation Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold text-white capitalize">
          {format(currentDate, "MMMM yyyy", { locale: ptBR })}
        </h2>
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onDateChange(subMonths(currentDate, 1))}
            className="w-10 h-10 rounded-xl bg-nectar-card border border-nectar-border text-slate-400 hover:text-white hover:border-brand-cyan/30 transition-all flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onDateChange(new Date())}
            className="px-4 h-10 rounded-xl bg-nectar-card border border-nectar-border text-slate-300 hover:text-white hover:border-brand-cyan/30 transition-all text-sm font-medium"
          >
            Hoje
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onDateChange(addMonths(currentDate, 1))}
            className="w-10 h-10 rounded-xl bg-nectar-card border border-nectar-border text-slate-400 hover:text-white hover:border-brand-cyan/30 transition-all flex items-center justify-center"
          >
            <ChevronRight className="w-5 h-5" />
          </motion.button>
        </div>
      </div>

      {/* Days Header */}
      <div className="grid grid-cols-7 gap-2">
        {["D", "S", "T", "Q", "Q", "S", "S"].map((day, idx) => (
          <div key={idx} className="text-center text-sm font-medium text-slate-500 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Days Grid */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((day, idx) => {
          const items = getItemsForDay(day);
          const totalItems = getTotalItems(items);
          const isCurrentDay = isToday(day);
          const inMonth = isCurrentMonth(day);

          return (
            <motion.div
              key={idx}
              whileHover={inMonth ? { scale: 1.02 } : {}}
              onClick={() => inMonth && onDayClick?.(day, items)}
              className={`aspect-square rounded-xl border-2 p-2 cursor-pointer transition-all flex flex-col ${
                isCurrentDay 
                  ? "bg-brand-cyan/5 border-brand-cyan" 
                  : inMonth 
                    ? "bg-nectar-card border-nectar-border hover:border-white/20" 
                    : "bg-transparent border-transparent opacity-30 cursor-default"
              }`}
            >
              {/* Day Number */}
              <div className="flex items-start justify-between">
                <span className={`text-lg font-bold ${
                  isCurrentDay ? "text-brand-cyan" : inMonth ? "text-white" : "text-slate-600"
                }`}>
                  {format(day, "d")}
                </span>
                {totalItems > 0 && inMonth && (
                  <span className="text-[10px] text-slate-500">{totalItems}</span>
                )}
              </div>

              {/* Indicator Dots */}
              {inMonth && totalItems > 0 && (
                <div className="flex-1 flex items-end justify-center">
                  <div className="flex gap-0.5 flex-wrap justify-center max-w-full">
                    {items.tasks.length > 0 && (
                      <div className={`w-2 h-2 rounded-full ${CATEGORY_COLORS.tasks.color}`} />
                    )}
                    {items.reminders.length > 0 && (
                      <div className={`w-2 h-2 rounded-full ${CATEGORY_COLORS.reminders.color}`} />
                    )}
                    {items.habits.length > 0 && (
                      <div className={`w-2 h-2 rounded-full ${CATEGORY_COLORS.habits.color}`} />
                    )}
                    {items.events.length > 0 && (
                      <div className={`w-2 h-2 rounded-full ${CATEGORY_COLORS.events.color}`} />
                    )}
                    {items.income.length > 0 && (
                      <div className={`w-2 h-2 rounded-full ${CATEGORY_COLORS.income.color}`} />
                    )}
                    {items.expense.length > 0 && (
                      <div className={`w-2 h-2 rounded-full ${CATEGORY_COLORS.expense.color}`} />
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 flex-wrap pt-4 border-t border-nectar-border">
        {Object.entries(CATEGORY_COLORS).map(([key, { color, label, textColor }]) => (
          activeFilters.includes(key) && (
            <div key={key} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${color}`} />
              <span className={`text-sm ${textColor}`}>{label}</span>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

// ==================== MAIN CALENDAR SECTION ====================

export default function CalendarSection() {
  // State
  const [connectionStatus, setConnectionStatus] = useState({ loading: true, connected: false, configured: false, email: null });
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [habits, setHabits] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [view, setView] = useState("month"); // overview, list, week, month
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filter, setFilter] = useState("all"); // all, today, week
  const [activeFilters, setActiveFilters] = useState(["tasks", "reminders", "habits", "events", "income", "expense"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayItems, setSelectedDayItems] = useState(null);

  // User ID
  const userId = useMemo(() => {
    const stored = localStorage.getItem('nexo_user_id');
    if (stored) return stored;
    const newId = `user_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('nexo_user_id', newId);
    return newId;
  }, []);

  // Check connection status
  const checkConnection = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/google/status?user_id=${userId}`, { headers: _authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setConnectionStatus({
          loading: false,
          connected: data.connected,
          configured: data.configured,
          email: data.email
        });
        return data.connected;
      }
    } catch (err) {
      console.error("Failed to check connection:", err);
    }
    setConnectionStatus(prev => ({ ...prev, loading: false }));
    return false;
  }, [userId]);

  // Fetch tasks, reminders, transactions, and habits
  const fetchAllData = useCallback(async () => {
    setIsLoadingTasks(true);
    try {
      // Fetch tasks
      const tasksRes = await fetch(`${API_URL}/api/tasks`, { headers: _authHeaders() });
      if (tasksRes.ok) {
        const tasksData = await tasksRes.json();
        setTasks(tasksData.tasks || []);
      }

      // Fetch reminders
      const remindersRes = await fetch(`${API_URL}/api/reminders`, { headers: _authHeaders() });
      if (remindersRes.ok) {
        const remindersData = await remindersRes.json();
        setReminders(remindersData.reminders || []);
      }

      // Fetch transactions
      const transactionsRes = await fetch(`${API_URL}/api/finance/transactions`, { headers: _authHeaders() });
      if (transactionsRes.ok) {
        const transactionsData = await transactionsRes.json();
        setTransactions(transactionsData.transactions || []);
      }

      // Fetch habits
      const habitsRes = await fetch(`${API_URL}/api/habits`, { headers: _authHeaders() });
      if (habitsRes.ok) {
        const habitsData = await habitsRes.json();
        setHabits(habitsData.habits || []);
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setIsLoadingTasks(false);
    }
  }, []);

  // Fetch Google Calendar events
  const fetchEvents = useCallback(async () => {
    if (!connectionStatus.connected) return;
    
    setIsLoadingEvents(true);
    try {
      // Get events for current month range (for month view)
      const start = format(startOfMonth(subMonths(currentDate, 1)), "yyyy-MM-dd");
      const end = format(endOfMonth(addMonths(currentDate, 1)), "yyyy-MM-dd");
      
      const response = await fetch(`${API_URL}/api/calendar/events?user_id=${userId}&start=${start}&end=${end}`, { headers: _authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setEvents(data.events || []);
      }
    } catch (err) {
      console.error("Failed to fetch events:", err);
    } finally {
      setIsLoadingEvents(false);
    }
  }, [userId, currentDate, connectionStatus.connected]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      // Always load all data (they don't need Google Calendar)
      fetchAllData();
      
      // Check Google Calendar connection
      const connected = await checkConnection();
      if (connected) {
        await fetchEvents();
      }
    };
    init();

    // Check URL params for OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_connected") === "true") {
      toast.success("Google Calendar conectado!");
      window.history.replaceState({}, document.title, window.location.pathname);
      checkConnection().then(connected => {
        if (connected) fetchEvents();
      });
    }
  }, [checkConnection, fetchEvents, fetchAllData]);

  // Refetch when date changes
  useEffect(() => {
    if (connectionStatus.connected) {
      fetchEvents();
    }
  }, [currentDate, connectionStatus.connected, fetchEvents]);

  // Delete event
  const handleDeleteEvent = async (event) => {
    if (!confirm("Deseja excluir este evento?")) return;
    
    try {
      const response = await fetch(`${API_URL}/api/calendar/events/${event.id}?user_id=${userId}`, {
        method: "DELETE",
        headers: _authHeaders(),
      });
      if (response.ok) {
        toast.success("Evento excluído");
        setSelectedEvent(null);
        fetchEvents();
      } else {
        toast.error("Erro ao excluir evento");
      }
    } catch (err) {
      toast.error("Falha na conexão");
    }
  };

  // Disconnect
  const handleDisconnect = async () => {
    if (!confirm("Deseja desconectar sua conta Google?")) return;
    
    try {
      const response = await fetch(`${API_URL}/api/auth/google/disconnect?user_id=${userId}`, {
        method: "DELETE",
        headers: _authHeaders(),
      });
      if (response.ok) {
        toast.success("Google Calendar desconectado");
        setConnectionStatus(prev => ({ ...prev, connected: false, email: null }));
        setEvents([]);
      }
    } catch (err) {
      toast.error("Erro ao desconectar");
    }
  };

  // Filtered events
  const filteredEvents = useMemo(() => {
    let result = [...events];

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(e => 
        e.summary?.toLowerCase().includes(query) ||
        e.location?.toLowerCase().includes(query) ||
        e.description?.toLowerCase().includes(query)
      );
    }

    // Filter by date
    if (filter === "today") {
      result = result.filter(e => {
        try {
          return isToday(parseISO(e.start));
        } catch { return false; }
      });
    } else if (filter === "week") {
      const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
      const weekEnd = endOfWeek(new Date(), { weekStartsOn: 0 });
      result = result.filter(e => {
        try {
          const date = parseISO(e.start);
          return date >= weekStart && date <= weekEnd;
        } catch { return false; }
      });
    }

    // Sort by date
    result.sort((a, b) => {
      try {
        return parseISO(a.start).getTime() - parseISO(b.start).getTime();
      } catch { return 0; }
    });

    return result;
  }, [events, searchQuery, filter]);

  // Summary data
  const summaryData = useMemo(() => {
    const today = new Date();
    
    // Events
    const todayEvents = events.filter(e => {
      try { return isToday(parseISO(e.start)); } catch { return false; }
    });
    const weekEvents = events.filter(e => {
      try {
        const date = parseISO(e.start);
        const weekStart = startOfWeek(today, { weekStartsOn: 0 });
        const weekEnd = endOfWeek(today, { weekStartsOn: 0 });
        return date >= weekStart && date <= weekEnd;
      } catch { return false; }
    });
    const upcomingEvents = events.filter(e => {
      try {
        const date = parseISO(e.start);
        return date > today;
      } catch { return false; }
    }).slice(0, 5);
    const nextEvent = upcomingEvents[0];

    // Tasks for today
    const todayTasks = tasks.filter(t => {
      if (!t.datetime) return t.status !== "done"; // Tasks without date show if not done
      try { return isToday(parseISO(t.datetime)); } catch { return false; }
    });
    const pendingTasks = tasks.filter(t => t.status !== "done");

    // Reminders for today
    const todayReminders = reminders.filter(r => {
      if (!r.datetime) return !r.completed;
      try { return isToday(parseISO(r.datetime)) && !r.completed; } catch { return false; }
    });
    const pendingReminders = reminders.filter(r => !r.completed);

    return { 
      todayCount: todayEvents.length, 
      weekCount: weekEvents.length, 
      nextEvent, 
      todayEvents, 
      upcomingEvents,
      todayTasks,
      pendingTasks,
      todayReminders,
      pendingReminders
    };
  }, [events, tasks, reminders]);

  // Loading state
  if (connectionStatus.loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-brand-cyan animate-spin" />
      </div>
    );
  }

  // Show calendar interface (even without Google Calendar connected)
  // The calendar will show tasks and reminders, and prompt to connect for Google events

  // Connected - show calendar UI
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Calendário</h1>
          {connectionStatus.connected ? (
            <p className="text-sm text-slate-400 flex items-center gap-2">
              Google Calendar: <span className="text-brand-cyan">{connectionStatus.email}</span>
              <button onClick={handleDisconnect} className="text-xs text-slate-500 hover:text-status-error transition-colors">
                (Desconectar)
              </button>
            </p>
          ) : connectionStatus.configured ? (
            <div className="flex items-center gap-3">
              <p className="text-sm text-slate-400">
                Tarefas e lembretes • <span className="text-amber-400">Google Calendar não conectado</span>
              </p>
              <motion.button
                onClick={async () => {
                  try {
                    const r = await fetch(
                      `${API_URL}/api/auth/google/start?user_id=${userId}`,
                      { headers: _authHeaders() }
                    );
                    if (!r.ok) {
                      toast.error(`Erro ${r.status} ao iniciar OAuth`);
                      return;
                    }
                    const data = await r.json();
                    if (data?.authorization_url) {
                      window.location.href = data.authorization_url;
                    } else {
                      toast.error("Backend não retornou authorization_url");
                    }
                  } catch (e) {
                    toast.error(e?.message || "Falha de rede");
                  }
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-3 py-1.5 bg-brand-cyan/10 border border-brand-cyan/30 text-brand-cyan rounded-lg text-xs font-medium hover:bg-brand-cyan/20 transition-all flex items-center gap-1.5"
                data-testid="connect-google-calendar-btn"
              >
                <Link2 className="w-3.5 h-3.5" />
                Conectar
              </motion.button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <p className="text-sm text-slate-400">
                Tarefas e lembretes • <span className="text-amber-500">Credenciais não configuradas</span>
              </p>
              <span className="px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-lg text-xs font-medium flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" />
                Configure nos Secrets
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center bg-nectar-card border border-nectar-border rounded-xl p-1">
            {[
              { id: "overview", icon: Grid3X3, label: "Visão Geral" },
              { id: "list", icon: List, label: "Lista" },
              { id: "week", icon: CalendarDays, label: "Semana" },
              { id: "month", icon: CalendarRange, label: "Mês" },
            ].map(v => (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                disabled={!connectionStatus.connected && (v.id === "week" || v.id === "month")}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  view === v.id ? "bg-brand-cyan/10 text-brand-cyan" : "text-slate-400 hover:text-white"
                } ${!connectionStatus.connected && (v.id === "week" || v.id === "month") ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <v.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{v.label}</span>
              </button>
            ))}
          </div>

          {/* Create Event Button (only if connected) */}
          {connectionStatus.connected && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsCreateModalOpen(true)}
              className="px-4 py-2 bg-brand-cyan/10 border border-brand-cyan/30 text-brand-cyan rounded-xl font-medium hover:bg-brand-cyan/20 transition-all flex items-center gap-2"
              data-testid="create-event-btn"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Criar Evento</span>
            </motion.button>
          )}
        </div>
      </div>

      {/* Overview View */}
      {view === "overview" && (
        <div className="space-y-6">
          {/* Google Calendar Connect Card (if not connected) */}
          {!connectionStatus.connected && (
            <ConnectPrompt userId={userId} isConfigured={connectionStatus.configured} />
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryCard
              title="Tarefas Pendentes"
              value={summaryData.pendingTasks?.length || 0}
              subtitle="a fazer"
              icon={CheckSquare}
              color="cyan"
            />
            <SummaryCard
              title="Lembretes"
              value={summaryData.pendingReminders?.length || 0}
              subtitle="pendentes"
              icon={Bell}
              color="purple"
            />
            <SummaryCard
              title="Eventos Hoje"
              value={summaryData.todayCount}
              subtitle={connectionStatus.connected ? "do Google Calendar" : "conecte o Calendar"}
              icon={Calendar}
              color="green"
            />
            <SummaryCard
              title="Esta Semana"
              value={summaryData.weekCount + (summaryData.todayTasks?.length || 0)}
              subtitle="compromissos"
              icon={CalendarDays}
              color="amber"
            />
          </div>

          {/* Next Event Highlight (only if connected and has event) */}
          {connectionStatus.connected && summaryData.nextEvent && (
            <NextEventHighlight event={summaryData.nextEvent} />
          )}

          {/* Grid with Tasks, Reminders, and Events */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Tasks Today */}
            <div className="bg-nectar-card border border-nectar-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-brand-cyan" />
                  <h3 className="text-lg font-medium text-white">Tarefas</h3>
                </div>
                <span className="text-xs text-slate-500">
                  {summaryData.todayTasks?.length || 0} para hoje
                </span>
              </div>
              {isLoadingTasks ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 text-brand-cyan animate-spin" />
                </div>
              ) : summaryData.todayTasks?.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Nenhuma tarefa para hoje</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {summaryData.todayTasks.slice(0, 5).map((task, idx) => (
                    <TaskCard key={task.id || idx} task={task} compact />
                  ))}
                </div>
              )}
            </div>

            {/* Reminders Today */}
            <div className="bg-nectar-card border border-nectar-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-brand-purple" />
                  <h3 className="text-lg font-medium text-white">Lembretes</h3>
                </div>
                <span className="text-xs text-slate-500">
                  {summaryData.todayReminders?.length || 0} pendentes
                </span>
              </div>
              {isLoadingTasks ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 text-brand-purple animate-spin" />
                </div>
              ) : summaryData.todayReminders?.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Nenhum lembrete pendente</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {summaryData.todayReminders.slice(0, 5).map((reminder, idx) => (
                    <ReminderCard key={reminder.id || idx} reminder={reminder} compact />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Google Calendar Events (only if connected) */}
          {connectionStatus.connected && (
            <>
              {/* Today's Events */}
              <div className="bg-nectar-card border border-nectar-border rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-brand-green" />
                    <h3 className="text-lg font-medium text-white">Eventos do Google Calendar</h3>
                  </div>
                  <button
                    onClick={() => { setView("list"); setFilter("today"); }}
                    className="text-sm text-brand-cyan hover:underline"
                  >
                    Ver todos
                  </button>
                </div>
                {isLoadingEvents ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 text-brand-cyan animate-spin" />
                  </div>
                ) : summaryData.todayEvents.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">Nenhum evento para hoje</p>
                ) : (
                  <div className="space-y-2">
                    {summaryData.todayEvents.slice(0, 5).map((event, idx) => (
                      <EventCard 
                        key={event.id || idx} 
                        event={event} 
                        compact 
                        onClick={setSelectedEvent}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Upcoming Events */}
              <div className="bg-nectar-card border border-nectar-border rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-white">Próximos Eventos</h3>
                  <button
                    onClick={() => { setView("list"); setFilter("all"); }}
                    className="text-sm text-brand-cyan hover:underline"
                  >
                    Ver todos
                  </button>
                </div>
                {summaryData.upcomingEvents.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">Nenhum evento futuro</p>
                ) : (
                  <div className="space-y-2">
                    {summaryData.upcomingEvents.map((event, idx) => (
                      <EventCard 
                        key={event.id || idx} 
                        event={event} 
                        compact 
                        onClick={setSelectedEvent}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* List View */}
      {view === "list" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Buscar eventos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-nectar-card border border-nectar-border rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-brand-cyan/50 transition-colors"
              />
            </div>
            <div className="flex items-center gap-2">
              {[
                { id: "all", label: "Todos" },
                { id: "today", label: "Hoje" },
                { id: "week", label: "Semana" },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    filter === f.id 
                      ? "bg-brand-cyan/10 text-brand-cyan border border-brand-cyan/30" 
                      : "text-slate-400 hover:text-white border border-transparent"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Events List */}
          {isLoadingEvents ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-brand-cyan animate-spin" />
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">Nenhum evento encontrado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEvents.map((event, idx) => (
                <EventCard
                  key={event.id || `event-${idx}`}
                  event={event}
                  onClick={setSelectedEvent}
                  onDelete={handleDeleteEvent}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Week View */}
      {view === "week" && (
        <div className="bg-nectar-card border border-nectar-border rounded-xl p-5">
          <WeekView
            events={events}
            currentDate={currentDate}
            onDateChange={setCurrentDate}
            onEventClick={setSelectedEvent}
          />
        </div>
      )}

      {/* Month View */}
      {view === "month" && (
        <div className="space-y-4">
          {/* Category Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-400 mr-2">Filtrar:</span>
            {Object.entries(CATEGORY_COLORS).map(([key, { color, label }]) => (
              <motion.button
                key={key}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setActiveFilters(prev => 
                    prev.includes(key) 
                      ? prev.filter(f => f !== key)
                      : [...prev, key]
                  );
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-all ${
                  activeFilters.includes(key)
                    ? "bg-white/5 border-white/20 text-white"
                    : "bg-transparent border-transparent text-slate-500 opacity-50"
                }`}
              >
                <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
                {label}
              </motion.button>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="bg-nectar-card border border-nectar-border rounded-2xl p-6">
            <MonthView
              events={events}
              tasks={tasks}
              reminders={reminders}
              habits={habits}
              transactions={transactions}
              currentDate={currentDate}
              onDateChange={setCurrentDate}
              onDayClick={(day, items) => {
                setSelectedDay(day);
                setSelectedDayItems(items);
              }}
              activeFilters={activeFilters}
            />
          </div>
        </div>
      )}

      {/* Day Detail Modal */}
      <DayDetailModal
        day={selectedDay}
        items={selectedDayItems}
        isOpen={!!selectedDay}
        onClose={() => {
          setSelectedDay(null);
          setSelectedDayItems(null);
        }}
        onEventClick={setSelectedEvent}
      />

      {/* Create Event Modal */}
      <CreateEventModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={() => fetchEvents()}
        userId={userId}
      />

      {/* Event Detail Modal */}
      <EventDetailModal
        event={selectedEvent}
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onDelete={handleDeleteEvent}
      />
    </div>
  );
}
